import EventScope from 'owa-service/lib/contract/EventScope';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { DateRange } from 'owa-datetime-utils';
import { EventsCacheLockId } from 'owa-events-cache';
import { fetchCalendarEventsForDateRange } from './fetchCalendarEventsForDateRange';
import {
    fetchFullCalendarEvent as fetchFullCalendarEventNew,
    fetchFullSeriesMasterCalendarEventFromEventInstance,
} from './fetchFullCalendarEventNEW';
import { fetchFullCalendarEvent } from './fetchFullCalendarEvent';
import { LockedCalendarEventsStore } from '../../types/LockedCalendarEventsStore';
import { releaseLock } from '../../actions/eventsCacheActions';
import { subscribeNotifications } from '../CalendarItemNotifications';
import { FetchFullCalendarEventCustomData } from '../../types/FetchFullCalendarEventCustomData';
import { ClientItemId } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { CalendarEventItemResponseShapeType } from '../../store/schema/CalendarEventItemResponseShapeType';

export function getLockedCalendarEventsStore(lockId: EventsCacheLockId): LockedCalendarEventsStore {
    if (!lockId) {
        throw new Error('The lock id should be provided');
    }

    return {
        fetchCalendarEventsForDateRange: (
            folderId: string,
            dateRange: DateRange
        ): { fetchPromise: Promise<CalendarEvent[]>; events: CalendarEvent[] | null } => {
            return fetchCalendarEventsForDateRange(lockId, folderId, dateRange);
        },
        subscribeNotifications: (folderId: string): void => {
            return subscribeNotifications(folderId, lockId);
        },
        fetchFullCalendarEvent: (
            eventId: ClientItemId,
            folderId?: string,
            getExistingCalendarEvent?: (eventId: ClientItemId) => CalendarEvent | null,
            // TODO VSO 82211: `cal-cache-update` flight clean up, remove eventScope param
            eventScope?:
                | EventScope.Default
                | EventScope.ThisInstanceOnly
                | EventScope.AllInstancesInSeries,
            customData?: FetchFullCalendarEventCustomData
        ): { event: CalendarEvent; fullEventPromise: Promise<CalendarEvent> } => {
            return fetchFullCalendarEventInternal(
                lockId,
                eventId,
                folderId,
                getExistingCalendarEvent,
                eventScope,
                customData
            );
        },
        fetchFullCalendarEventAndUpdateItemResponseShapeType: (
            eventId: ClientItemId,
            itemResponseShapeType: CalendarEventItemResponseShapeType,
            folderId?: string,
            getExistingCalendarEvent?: (eventId: ClientItemId) => CalendarEvent | null,
            // TODO VSO 82211: `cal-cache-update` flight clean up, remove eventScope param
            eventScope?:
                | EventScope.Default
                | EventScope.ThisInstanceOnly
                | EventScope.AllInstancesInSeries,
            customData?: FetchFullCalendarEventCustomData
        ): { event: CalendarEvent; fullEventPromise: Promise<CalendarEvent> } => {
            return fetchFullCalendarEventInternal(
                lockId,
                eventId,
                folderId,
                getExistingCalendarEvent,
                eventScope,
                customData,
                itemResponseShapeType
            );
        },
        // TODO VSO 82211: `cal-cache-update` flight clean up, when this flight is rolled out,
        // move cache consumers who are fetching full series master calendar events from an event instance
        // (aka the consumers where eventScope === EventScope.AllInstancesInSeries) to using a destinct entry point

        // fetchFullSeriesMasterCalendarEventFromEventInstance: (
        //     eventId: ClientItemId,
        //     folderId?: string,
        //     getExistingCalendarEvent?: (eventId: ClientItemId) => CalendarEvent | null,
        //     customData?: FetchFullCalendarEventCustomData
        // ): { event: CalendarEvent; fullEventPromise: Promise<CalendarEvent> } => {
        //     return fetchFullSeriesMasterCalendarEventFromEventInstance(
        //         lockId,
        //         eventId,
        //         folderId,
        //         getExistingCalendarEvent,
        //         customData
        //     );
        // },
        getLockId: () => lockId,
        release: () => releaseLock(lockId),
    };
}

function fetchFullCalendarEventInternal(
    lockId: EventsCacheLockId,
    eventId: ClientItemId,
    folderId?: string,
    getExistingCalendarEvent?: (eventId: ClientItemId) => CalendarEvent | null,
    // TODO VSO 82211: `cal-cache-update` flight clean up, remove eventScope param
    eventScope?: EventScope.Default | EventScope.ThisInstanceOnly | EventScope.AllInstancesInSeries,
    customData?: FetchFullCalendarEventCustomData,
    itemResponseShapeType?: CalendarEventItemResponseShapeType
) {
    const isFetchingSeriesMaster = eventScope === EventScope.AllInstancesInSeries;
    if (isFeatureEnabled('cal-cache-update')) {
        const promise = isFetchingSeriesMaster
            ? fetchFullSeriesMasterCalendarEventFromEventInstance(
                  lockId,
                  eventId,
                  folderId,
                  getExistingCalendarEvent /* getExistingCalendarEvent */,
                  customData,
                  itemResponseShapeType
              )
            : fetchFullCalendarEventNew(
                  lockId,
                  eventId,
                  folderId,
                  getExistingCalendarEvent /* getExistingCalendarEvent */,
                  customData,
                  itemResponseShapeType
              );
        return promise;
    } else {
        return fetchFullCalendarEvent(
            lockId,
            eventId,
            folderId,
            getExistingCalendarEvent,
            eventScope,
            customData,
            itemResponseShapeType
        );
    }
}
